import { Injectable } from '@angular/core';
import { TranslateService } from "@ngx-translate/core";
import {
  resendSignUpCode,
  resetPassword,
  ResetPasswordInput,
  SignUpInput,
} from "aws-amplify/auth";
import { normalizeEmail } from "../utils";

/**
 * Cognito decides which language to send its sign-up / password-reset emails in from two
 * places, and the pool reads both: the `locale` user attribute, which stays on the user, and
 * `clientMetadata.locale`, which is per request and is the only source for users created
 * before we started sending the attribute. The backend normalises whatever it gets
 * (`de-CH`, `IT`, `fr_FR`, …) and falls back to German, so an unexpected value cannot break
 * the call.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthLocaleService {
  constructor(private translate: TranslateService) {
  }

  get locale(): string {
    return this.translate.getCurrentLang() || localStorage.getItem('lang') || 'de';
  }

  get clientMetadata(): { locale: string } {
    return { locale: this.locale };
  }

  // The email is the username, and the authenticator sends it in both places.
  signUpInput(input: SignUpInput): SignUpInput {
    const userAttributes = { ...input.options?.userAttributes, locale: this.locale };

    if (userAttributes.email) {
      userAttributes.email = normalizeEmail(userAttributes.email);
    }

    return {
      ...input,
      username: normalizeEmail(input.username),
      options: {
        ...input.options,
        userAttributes,
        clientMetadata: { ...input.options?.clientMetadata, locale: this.locale },
      },
    };
  }

  resetPassword(input: ResetPasswordInput) {
    return resetPassword({
      ...input,
      options: {
        ...input.options,
        clientMetadata: { ...input.options?.clientMetadata, locale: this.locale },
      },
    });
  }

  resendSignUpCode(username: string) {
    return resendSignUpCode({ username, options: { clientMetadata: this.clientMetadata } });
  }
}
